// src/pages/Dashboard.jsx
//
// The credentialed clinician view of the demo case library.
// Surface copy is intentionally clinical and non-technical — no model names,
// no dataset names, no probability jargon. The underlying API contract is
// unchanged; this file only changes labels, headings, and verdict phrasing.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import MriViewer from '../components/MriViewer';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, ApiError } from '../api';
import { useSessionTracker } from '../hooks/useSessionTracker';
import './Dashboard.css';

// ---------------------------------------------------------------------------
// Display maps
// ---------------------------------------------------------------------------

// Backend label_category → clinician-facing description.
// Colour keys match .cat-badge--ad/cn/smci/pmci in Dashboard.css.
const CATEGORY_META = {
  AD_stable: { label: "Alzheimer's disease",          color: 'ad'   },
  CN_stable: { label: 'Cognitively normal',           color: 'cn'   },
  sMCI:      { label: 'Mild impairment, stable',      color: 'smci' },
  pMCI:      { label: 'Progressed to dementia',       color: 'pmci' },
};

// Raw visit codes → clean clinical labels.
const VISIT_LABEL = {
  bl:  'Baseline',
  m06: '6-month follow-up',
  m12: '12-month follow-up',
  m18: '18-month follow-up',
  m24: '24-month follow-up',
  m36: '36-month follow-up',
  m48: '48-month follow-up',
};

// Raw cohort groups → short clinical descriptors. CN/MCI/AD are the standard
// research labels; we translate them to plain language for the UI.
function groupDescriptor(group) {
  const g = (group || '').toUpperCase();
  if (g === 'CN') return { label: 'Normal cognition', color: 'cn'  };
  if (g === 'AD') return { label: "Alzheimer's",      color: 'ad'  };
  // EMCI / LMCI / SMC / MCI all map to one bucket clinically.
  if (g === 'MCI' || g === 'EMCI' || g === 'LMCI' || g === 'SMC') {
    return { label: 'Mild impairment', color: 'mci' };
  }
  return { label: group || '—', color: 'mci' };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Dashboard({ theme, toggleTheme }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { logEvent } = useSessionTracker(user);

  const [samples, setSamples] = useState([]);
  const [disclaimer, setDisclaimer] = useState(
    'For research and demonstration purposes only. Not a medical device. Not for clinical use.'
  );

  const [selectedId, setSelectedId] = useState(null);
  const [prediction, setPrediction] = useState(null);

  const [loadingSamples, setLoadingSamples] = useState(true);
  const [loadingPrediction, setLoadingPrediction] = useState(false);
  const [samplesError, setSamplesError] = useState(null);
  const [predictionError, setPredictionError] = useState(null);

  const handleAuthError = useCallback(() => {
    navigate('/login', { replace: true });
  }, [navigate]);

  // ---- initial fetch ----
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      setLoadingSamples(true);
      setSamplesError(null);
      try {
        const data = await apiGet('/api/samples', user);
        if (cancelled) return;
        const list = Array.isArray(data?.samples) ? data.samples : [];
        setSamples(list);
        if (data?.disclaimer) setDisclaimer(data.disclaimer);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          handleAuthError();
          return;
        }
        setSamplesError(
          err instanceof ApiError
            ? err.detail || err.message
            : 'Could not load the case library.'
        );
      } finally {
        if (!cancelled) setLoadingSamples(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, handleAuthError]);

  // ---- selection ----
  const handleSelect = useCallback(
    async (demoId) => {
      if (!user || demoId === selectedId) return;
      setSelectedId(demoId);
      setPrediction(null);
      setPredictionError(null);
      setLoadingPrediction(true);

      const sample = samples.find((s) => s.demo_id === demoId);
      logEvent('case_view', {
        demo_id: demoId,
        subject_id: sample?.subject_id,
        label_category: sample?.label_category,
      });

      try {
        const data = await apiGet(
          `/api/predict/${encodeURIComponent(demoId)}`,
          user
        );
        setPrediction(data);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          handleAuthError();
          return;
        }
        setPredictionError(
          err instanceof ApiError
            ? err.detail || err.message
            : 'Could not retrieve the assessment for this case.'
        );
      } finally {
        setLoadingPrediction(false);
      }
    },
    [user, selectedId, samples, logEvent, handleAuthError]
  );

  // Index of the selected case — used to label as "Case 1, Case 2…" rather
  // than exposing the raw ADNI subject identifier.
  const selectedIndex = samples.findIndex((s) => s.demo_id === selectedId);

  return (
    <>
      <Navbar theme={theme} toggleTheme={toggleTheme} />

      {/* Disclaimer banner — gold, full width, immediately below the navbar */}
      <div className="dashboard-disclaimer" role="note" aria-label="Disclaimer">
        <span className="dashboard-disclaimer__icon" aria-hidden>⚠</span>
        <span className="dashboard-disclaimer__text">{disclaimer}</span>
      </div>

      <main className="dashboard">
        <div className="container">
          <div className="dashboard__head">
            <span className="eyebrow">Sample library</span>
            <h1 className="dashboard__title">Anonymized clinical cases</h1>
            <p className="dashboard__lead">
              Browse a curated set of longitudinal MRI cases. For each one you
              can review the baseline and follow-up scans, see the BrainAxis
              assessment of progression risk, and compare it to the patient's
              confirmed clinical outcome.
            </p>
          </div>

          {/* ---------- Sample picker ---------- */}
          <section className="dashboard__section">
            <h2 className="dashboard__section-title">Select a case</h2>

            {loadingSamples ? (
              <div className="sample-row">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="sample-chip sample-chip--skeleton" />
                ))}
              </div>
            ) : samplesError ? (
              <div className="dashboard__error">{samplesError}</div>
            ) : samples.length === 0 ? (
              <div className="dashboard__empty">No cases available.</div>
            ) : (
              <div className="sample-row">
                {samples.map((s, i) => (
                  <SampleChip
                    key={s.demo_id}
                    sample={s}
                    index={i}
                    selected={s.demo_id === selectedId}
                    onClick={() => handleSelect(s.demo_id)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* ---------- MRI viewer ---------- */}
          {selectedId && (
            <section className="dashboard__section">
              <h2 className="dashboard__section-title">MRI scans</h2>
              <MriViewer
                demoId={selectedId}
                user={user}
                onAuthError={handleAuthError}
              />
            </section>
          )}

          {/* ---------- Result panel ---------- */}
          {selectedId && (
            <section className="dashboard__section">
              <h2 className="dashboard__section-title">Risk assessment</h2>

              {loadingPrediction ? (
                <div className="result-panel result-panel--skeleton" />
              ) : predictionError ? (
                <div className="dashboard__error">{predictionError}</div>
              ) : prediction ? (
                <ResultPanel
                  p={prediction}
                  caseNumber={selectedIndex >= 0 ? selectedIndex + 1 : null}
                />
              ) : null}
            </section>
          )}
        </div>
      </main>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SampleChip({ sample, index, selected, onClick }) {
  const meta = CATEGORY_META[sample.label_category] || {
    label: sample.label_category,
    color: 'neutral',
  };
  const baseline = VISIT_LABEL[sample.baseline_visit] || sample.baseline_visit;
  const followup = VISIT_LABEL[sample.followup_visit] || sample.followup_visit;

  return (
    <button
      type="button"
      className={`sample-chip ${selected ? 'sample-chip--selected' : ''}`}
      onClick={onClick}
      aria-pressed={selected}
    >
      <div className="sample-chip__subject">Case {index + 1}</div>
      <div className={`cat-badge cat-badge--${meta.color}`}>{meta.label}</div>
      <div className="sample-chip__visit">
        {baseline} <span aria-hidden>→</span> {followup}
      </div>
    </button>
  );
}

function ResultPanel({ p, caseNumber }) {
  const prob = typeof p.predicted_probability === 'number'
    ? p.predicted_probability
    : 0;
  const probClamped = Math.max(0, Math.min(1, prob));
  const probPct = probClamped * 100;

  const predIsConverter = p.predicted_class === 'converter';
  const truthIsConverter = p.ground_truth_label === 1;
  const truthDescriptor = truthIsConverter ? 'progressed to dementia' : 'remained stable';
  const agree = predIsConverter === truthIsConverter;

  const baselineVisit = VISIT_LABEL[p.baseline_visit] || p.baseline_visit;
  const followupVisit = VISIT_LABEL[p.followup_visit] || p.followup_visit;

  const baselineGroup = groupDescriptor(p.baseline_group);
  const followupGroup = groupDescriptor(p.followup_group);

  return (
    <article className="result-panel">
      <header className="result-panel__head">
        <div>
          <div className="result-panel__subject">
            {caseNumber != null ? `Case ${caseNumber}` : 'Selected case'}
          </div>
          <div className="result-panel__visit">
            {baselineVisit} → {followupVisit}
            {p.interval_months != null && (
              <> · {p.interval_months} months between scans</>
            )}
          </div>
        </div>
        <div className="result-panel__groups">
          <span className={`group-pill group-pill--${baselineGroup.color}`}>
            {baselineGroup.label}
          </span>
          <span className="result-panel__arrow" aria-hidden>→</span>
          <span className={`group-pill group-pill--${followupGroup.color}`}>
            {followupGroup.label}
          </span>
        </div>
      </header>

      {/* Risk bar */}
      <div className="prob-bar">
        <div className="prob-bar__label">
          <span>Risk of progression</span>
          <span className="prob-bar__value">{probPct.toFixed(1)}%</span>
        </div>
        <div
          className="prob-bar__track"
          role="progressbar"
          aria-valuenow={Math.round(probPct)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="prob-bar__fill" style={{ width: `${probPct}%` }} />
        </div>
        <div className="prob-bar__scale">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Assessment vs. confirmed outcome */}
      <div className="result-panel__verdict">
        <span
          className={`class-badge class-badge--${
            predIsConverter ? 'positive' : 'negative'
          }`}
        >
          {predIsConverter ? 'Likely to progress' : 'Likely to remain stable'}
        </span>
        <span className="result-panel__truth">
          Actual outcome: <strong>{truthDescriptor}</strong>
          {agree ? (
            <span
              className="result-panel__match result-panel__match--ok"
              title="Assessment agrees with the confirmed outcome"
              aria-label="Agreement"
            >
              ✓
            </span>
          ) : (
            <span
              className="result-panel__match result-panel__match--warn"
              title="Assessment differs from the confirmed outcome"
              aria-label="Disagreement"
            >
              ⚠
            </span>
          )}
        </span>
      </div>
    </article>
  );
}
